export const GEOJSON_POLYGON_SELF_INTERSECTION_ERROR =
	"Polygon rings must not cross themselves";

type Position = [number, number];

const positionsEqual = (left: Position, right: Position): boolean =>
	left[0] === right[0] && left[1] === right[1];

const orientation = (a: Position, b: Position, c: Position): number => {
	const value = (b[1] - a[1]) * (c[0] - b[0]) - (b[0] - a[0]) * (c[1] - b[1]);
	if (value > 0) return 1;
	if (value < 0) return -1;
	return 0;
};

const onSegment = (a: Position, b: Position, point: Position): boolean =>
	point[0] <= Math.max(a[0], b[0]) &&
	point[0] >= Math.min(a[0], b[0]) &&
	point[1] <= Math.max(a[1], b[1]) &&
	point[1] >= Math.min(a[1], b[1]);

const segmentsIntersect = (
	a: Position,
	b: Position,
	c: Position,
	d: Position,
): boolean => {
	if (
		positionsEqual(a, c) ||
		positionsEqual(a, d) ||
		positionsEqual(b, c) ||
		positionsEqual(b, d)
	) {
		return false;
	}

	const abC = orientation(a, b, c);
	const abD = orientation(a, b, d);
	const cdA = orientation(c, d, a);
	const cdB = orientation(c, d, b);

	if (abC !== abD && cdA !== cdB) return true;

	if (abC === 0 && onSegment(a, b, c)) return true;
	if (abD === 0 && onSegment(a, b, d)) return true;
	if (cdA === 0 && onSegment(c, d, a)) return true;
	if (cdB === 0 && onSegment(c, d, b)) return true;

	return false;
};

const openRing = (ring: Position[]): Position[] => {
	if (ring.length < 2) return ring;
	const first = ring[0]!;
	const last = ring[ring.length - 1]!;
	return positionsEqual(first, last) ? ring.slice(0, -1) : ring;
};

const ringHasSelfIntersection = (ring: unknown): boolean => {
	if (!Array.isArray(ring)) return false;

	const vertices = openRing(
		ring.filter(
			(value): value is Position =>
				Array.isArray(value) &&
				value.length >= 2 &&
				typeof value[0] === "number" &&
				typeof value[1] === "number",
		),
	);

	const vertexCount = vertices.length;
	if (vertexCount < 4) return false;

	for (let start = 0; start < vertexCount; start += 1) {
		const startNext = (start + 1) % vertexCount;
		for (let other = start + 1; other < vertexCount; other += 1) {
			const otherNext = (other + 1) % vertexCount;
			const isAdjacent =
				other === startNext || (start === 0 && other === vertexCount - 1);
			if (isAdjacent) continue;

			if (
				segmentsIntersect(
					vertices[start]!,
					vertices[startNext]!,
					vertices[other]!,
					vertices[otherNext]!,
				)
			) {
				return true;
			}
		}
	}

	return false;
};

const toPositions = (ring: unknown): Position[] => {
	if (!Array.isArray(ring)) return [];

	return openRing(
		ring.filter(
			(value): value is Position =>
				Array.isArray(value) &&
				value.length >= 2 &&
				typeof value[0] === "number" &&
				typeof value[1] === "number",
		),
	);
};

const isPointOnRing = (point: Position, ring: Position[]): boolean => {
	const vertexCount = ring.length;
	if (vertexCount === 0) return false;

	for (let index = 0; index < vertexCount; index += 1) {
		const start = ring[index]!;
		const end = ring[(index + 1) % vertexCount]!;
		if (positionsEqual(point, start) || positionsEqual(point, end)) {
			return true;
		}

		if (orientation(start, end, point) === 0 && onSegment(start, end, point)) {
			return true;
		}
	}

	return false;
};

const isPointInRing = (point: Position, ring: Position[]): boolean => {
	if (ring.length < 3) return false;
	if (isPointOnRing(point, ring)) return true;

	let isInside = false;
	const [x, y] = point;

	for (
		let index = 0, previous = ring.length - 1;
		index < ring.length;
		previous = index, index += 1
	) {
		const [currentX, currentY] = ring[index]!;
		const [previousX, previousY] = ring[previous]!;
		const straddlesY = currentY > y !== previousY > y;
		if (!straddlesY) continue;

		const intersectX =
			((previousX - currentX) * (y - currentY)) / (previousY - currentY) +
			currentX;
		if (x < intersectX) isInside = !isInside;
	}

	return isInside;
};

/** True when every ring is a simple (non-self-intersecting) linear ring. */
export const isSimpleGeoJsonPolygon = (polygon: {
	coordinates: unknown;
}): boolean => {
	if (!Array.isArray(polygon.coordinates) || polygon.coordinates.length === 0) {
		return false;
	}

	return !polygon.coordinates.some((ring) => ringHasSelfIntersection(ring));
};

/**
 * Point-in-polygon for GeoJSON polygons. Boundary points count as inside.
 * Interior rings (holes) are excluded.
 */
export const isPointInGeoJsonPolygon = (
	point: Position,
	polygon: { coordinates: unknown },
): boolean => {
	if (!Array.isArray(polygon.coordinates) || polygon.coordinates.length === 0) {
		return false;
	}

	const exterior = toPositions(polygon.coordinates[0]);
	if (!isPointInRing(point, exterior)) return false;

	return !polygon.coordinates
		.slice(1)
		.some((ring) => isPointInRing(point, toPositions(ring)));
};
